import { VerbRail } from '@components/VerbRail/VerbRail';
import { useLibraryVerbs } from '@lib/hooks/useLibraryVerbs';
import { verbPillItem } from '@lib/verbs';
import { Tooltip } from '@mui/material';
import { useLibrary } from '@stores/Library/Library';
import { observer } from 'mobx-react';
import css from './LibraryActionRow.module.scss';

interface LibraryActionRowProps {
  /**
   * Abbreviate to the bare icon rail. Set by the header, from a measurement of the WHOLE row —
   * see `VERB_LABELS_BELOW` in `LibraryContent`, and the note there on why this decision cannot
   * be made from the width of the title row these pills sit in.
   */
  collapsed?: boolean;
}

/**
 * The one labelled surface for the library-scoped verbs *inside* a library — the place the words
 * are taught here. Every other appearance of these verbs within a library is a bare icon rail
 * (`VerbRail`), which is legible only because this row exists.
 *
 * The outcome lines live in tooltips rather than under the labels: the row sits inline in the
 * library header, so it has to stay one control-height tall. They say what each click produces
 * and on what, which the labels alone leave open.
 *
 * Under `collapsed` it becomes a `VerbRail` — the same abbreviation this row's words teach, here
 * driven by available width rather than by surface. The rail's tooltips carry the labels but not
 * the outcome lines: once the words are gone, the name is what the user needs, and that matches
 * every other rail in the app.
 *
 * Worth knowing how often each form is on screen. The labelled one needs 790px of header row
 * (`VERB_LABELS_BELOW`) — roughly an 850px viewport with the nav rail collapsed, ~1060px with it
 * expanded, so most laptops show the words either way. It needed far more while the header's right
 * side also held autoplay, Filters, Sort, the view switch and Add media. The words are also taught
 * on the Libraries page (`LibrariesVerbPills`) and in the ⋯ menus, so neither form is the only
 * place a user can learn them.
 *
 * Deliberately not a header CTA in the page's own right: a control up there would have to answer
 * "which library?" — here the page itself is the answer, so nothing has to ask.
 */
export const LibraryActionRow = observer(function LibraryActionRow({
  collapsed,
}: LibraryActionRowProps) {
  const library = useLibrary();
  const { workWithAgent, findMoments } = useLibraryVerbs();

  const libraryId = library.libraryId;

  // Both verbs need only the id, so the row doesn't wait on `stats`. It hides
  // for a library that is genuinely empty (`EmptyLibrary` owns that state), and
  // fails OPEN while stats are undefined — a slow stats load must not blank out
  // the primary actions.
  if (!libraryId || library.stats?.count === 0) return null;

  // `scoped` outcome lines: this page IS one library, so they say "this library" rather than
  // "a library" (which is what the Libraries page's pills use).
  const actions = [
    {
      ...verbPillItem('work-with-agent', { scoped: true }),
      onClick: () =>
        workWithAgent(libraryId, { surface: 'library', control: 'library-action-row' }),
    },
    {
      ...verbPillItem('find-moments', { scoped: true }),
      onClick: () => findMoments(libraryId, { surface: 'library', control: 'library-action-row' }),
    },
  ];

  // The rail reads the same descriptors directly — it satisfies `VerbRailItem` as-is and simply
  // ignores the `outcome` lines it has no room for.
  //
  // Not fed from `buildLibraryRailItems`: that builder hands out the *unscoped* outcome and this
  // surface needs the scoped wording. (Not because of the `add-media` slot — omitting
  // `onAddMedia` already suppresses that.) Drift between the two forms is no longer a risk
  // either way: both take their identity from `@lib/verbs`.
  if (collapsed) {
    return <VerbRail variant="overlay" className={css.collapsedRail} items={actions} />;
  }

  return (
    <div className={css.row}>
      {actions.map((action) => (
        <Tooltip key={action.id} title={action.outcome} placement="bottom">
          <button
            type="button"
            className={action.accent ? css.cta : css.glass}
            onClick={action.onClick}
          >
            <span className={css.icon}>{action.icon}</span>
            {action.label}
          </button>
        </Tooltip>
      ))}
    </div>
  );
});
